import React, { useState } from "react";
import PropTypes from "prop-types";
import { TextField, Checkbox, Autocomplete } from "@mui/material";
import "./style.scss";
import { extractProperty } from "../../utils/commonUtils";
import { useResponsiveStyles } from "../../styles/useResponsiveStyles";

export const CustomSelect = ({ filter, selectedValue, onFilterChange }) => {
    const [inputValue, setInputValue] = useState("");
    const { getFontSize } = useResponsiveStyles();

    const handleInputChange = (event, newInputValue) => {
        setInputValue(newInputValue);
    };

    const handleChange = (event, newValue) => {
        onFilterChange(newValue);
        // Keep the input value cleared so it doesn't display in the text field
        setInputValue("");
    };

    return (
        <Autocomplete
            multiple={filter.multiSelect}
            options={
                filter?.defaultFilterRange?.length
                    ? filter.options.filter((val) =>
                          filter?.defaultFilterRange.includes(val.id)
                      )
                    : filter.options
            }
            value={selectedValue}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            filterSelectedOptions={false}
            disableCloseOnSelect={filter.multiSelect ? true : false}
            disableClearable
            clearOnEscape
            getOptionLabel={(option) => option.label || option}
            renderTags={() => null}
            renderOption={(props, option, { selected }) => (
                <li
                    {...props}
                    style={{ ...styles.listItem, ...getFontSize("body1") }}
                >
                    {filter.multiSelect && (
                        <Checkbox
                            checked={selected}
                            sx={{ ...styles.checkBox }}
                        />
                    )}
                    {extractProperty(option, "label")}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    placeholder={filter?.label}
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        style: { ...getFontSize("body1") },
                    }}
                    sx={{
                        ...styles.textField,
                        "& .MuiInputBase-input::placeholder": {
                            ...getFontSize("body1"),
                            fontStyle: "italic",
                            color: "#939598",
                            opacity: 1,
                            fontWeight: 700,
                            letterSpacing: 0.15,
                        },
                        "& .MuiInputBase-root": {
                            padding: "0px 4px",
                            margin: 0,
                        },
                        "& .MuiAutocomplete-input": {
                            padding: "0px 4px",
                            margin: 0,
                        },
                        ".MuiOutlinedInput-root": {
                            padding: "0px !important", // Reset outer padding
                        },
                    }}
                />
            )}
        />
    );
};

const styles = {
    listItem: {
        backgroundColor: "transparent",
        "&:hover": {
            backgroundColor: "transparent", // Remove hover background
        },
        "&.Mui-selected": {
            backgroundColor: "transparent", // Remove selected background
        },
    },
    checkBox: {
        color: "#ED1C24", // Default (unchecked) border color
        "&.Mui-checked": {
            color: "#ED1C24", // Checked color
        },
    },
    textField: {
        "& .MuiOutlinedInput-root": {
            "& fieldset": {
                borderColor: "#0000003B",
                padding: 0,
            },
            "&:hover fieldset": {
                borderColor: "#0000003B",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#0000003B",
            },
        },
    },
};

CustomSelect.propTypes = {
    filter: PropTypes.shape({
        name: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(
            PropTypes.oneOfType([PropTypes.string, PropTypes.object])
        ).isRequired,
        multiSelect: PropTypes.bool,
    }).isRequired,
    selectedValue: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(
            PropTypes.oneOfType([PropTypes.string, PropTypes.object])
        ),
    ]).isRequired,
    onFilterChange: PropTypes.func.isRequired,
};

export default CustomSelect;
